'use client';

import { useEffect, useState } from 'react';

/**
 * Returns `true` once the referenced element is about to enter the viewport
 * (IntersectionObserver). Intentionally does NOT activate on early page scroll
 * — that pulled HubSpot ~196KB into the PageSpeed TBT window. Explicit pointer
 * / keyboard on the form area still works via intersection when the user scrolls
 * near it.
 *
 * Once it flips to `true` it stays `true`.
 *
 * @param {React.RefObject<HTMLElement>} ref - element to observe
 * @param {{ rootMargin?: string, eager?: boolean }} [opts]
 *   eager — load immediately (lead forms); skip IntersectionObserver deferral.
 */
export function useDeferredVisible(ref, { rootMargin = '200px', eager = false } = {}) {
  const [active, setActive] = useState(eager);

  useEffect(() => {
    if (active || eager) return;
    if (typeof window === 'undefined') return;

    let cleanedUp = false;
    const activate = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      cleanup();
      setActive(true);
    };

    let observer;
    const el = ref?.current;
    if (el && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) activate();
        },
        { rootMargin }
      );
      observer.observe(el);
    } else {
      // No IO — load on first intentional pointer/key (not scroll).
      const onInteraction = () => activate();
      window.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
      window.addEventListener('keydown', onInteraction, { once: true, passive: true });
      return () => {
        window.removeEventListener('pointerdown', onInteraction);
        window.removeEventListener('keydown', onInteraction);
      };
    }

    function cleanup() {
      if (observer) observer.disconnect();
    }

    return cleanup;
  }, [ref, rootMargin, active, eager]);

  return active;
}
